/**
 * TanStack Query hooks and key factory for the vendor DTA dashboard.
 */

import { useInfiniteQuery, useQueries } from "@tanstack/react-query"
import type { DTAVersion } from "@/types/dta"
import { dtaApiService } from "@/services/dtaApi"

export const vendorDtaKeys = {
  all: ["vendor-dtas"] as const,
  lists: () => [...vendorDtaKeys.all, "list"] as const,
  list: (vendorId: string | undefined) => [...vendorDtaKeys.lists(), vendorId ?? null] as const,
  versions: (dtaId: string) => [...vendorDtaKeys.all, "versions", dtaId] as const,
}

const PAGE_SIZE = 20

/**
 * Fetches paginated DTAs the caller holds an external grant on. Uses infinite query to
 * progressively load all pages, enabling client-side pagination over the accumulated collection.
 */
export function useVendorDtasList(vendorId: string | undefined) {
  return useInfiniteQuery({
    queryKey: vendorDtaKeys.list(vendorId),
    queryFn: ({ pageParam }) =>
      dtaApiService.getVendorDTAs({
        limit: PAGE_SIZE,
        nextToken: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextToken ?? null,
    enabled: !!vendorId,
    refetchInterval: 5_000,
  })
}

interface DtaVersionsState {
  versions: DTAVersion[]
  isLoading: boolean
}

/**
 * Lazily fetches version history for each currently-expanded DTA row. Returns a map keyed by dtaId.
 */
export function useExpandedDtaVersions(dtaIds: string[]): Record<string, DtaVersionsState> {
  const results = useQueries({
    queries: dtaIds.map((dtaId) => ({
      queryKey: vendorDtaKeys.versions(dtaId),
      queryFn: () => dtaApiService.listVersions(dtaId),
    })),
  })

  const map: Record<string, DtaVersionsState> = {}
  dtaIds.forEach((dtaId, index) => {
    const result = results[index]
    map[dtaId] = {
      versions: result?.data?.versions ?? [],
      isLoading: result?.isLoading ?? false,
    }
  })
  return map
}
